import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Button,
  Divider,
  Typography,
  Box,
  Avatar,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  InputAdornment
} from '@mui/material';
import {
  Info as InfoIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Image as ImageIcon,
  Highlight as HighlightIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const EditAdditionalDetails = ({ onSaveKeyTakeaways, onSaveSpeakers, onRemoveSpeaker, event }) => {
  const [speakers, setSpeakers] = useState([]);
  const [keyTakeaways, setKeyTakeaways] = useState([]);
  const [newTakeaway, setNewTakeaway] = useState('');
  // Image file is stored in currentSpeaker.image (binary File); imagePreview holds the data URL for UI display
  const [speakerDialogOpen, setSpeakerDialogOpen] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState({
    id: null,
    name: '',
    organization: '',
    organizationRole: '',
    image: null,
    imagePreview: null
  });

  useEffect(() => {
    let counter = 0;
    const keyTakeawaysObject = event?.keyTakeaways?.map((takeaway) => {
      return {
        'id': counter++,
        'text': takeaway.trim()
      }
    }) || [];
    setKeyTakeaways(keyTakeawaysObject)
    setSpeakers(event.speakers)
  }, [event?.keyTakeaways, event?.speakers]);

  const handleSpeakerInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSpeaker(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentSpeaker(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const openSpeakerDialog = (speaker = null) => {
    if (speaker) {
      setCurrentSpeaker({
        id: speaker.id,
        name: speaker.name,
        organization: speaker.organization,
        organizationRole: speaker.organizationRole,
        image: null,
        imagePreview: speaker.image
      });
    } else {
      setCurrentSpeaker({
        id: null,
        name: '',
        organization: '',
        organizationRole: '',
        image: null,
        imagePreview: null
      });
    }
    setSpeakerDialogOpen(true);
  };

  const closeSpeakerDialog = () => {
    setSpeakerDialogOpen(false);
  };

  const saveSpeaker = () => {
    if (currentSpeaker.name.trim() === '') return;

    let updatedSpeakers;
    let savedSpeaker;
    
    if (currentSpeaker.id) {
      // Update existing speaker
      updatedSpeakers = speakers.map(speaker => 
        speaker.id === currentSpeaker.id 
          ? { 
              ...currentSpeaker,
              // Keep preview (data URL) for UI, but send binary via callback
              image: currentSpeaker.imagePreview
            } 
          : speaker
      );
      savedSpeaker = {
        ...currentSpeaker,
        // For update, pass binary file if a new one was selected; otherwise null to indicate no image change
        image: currentSpeaker.image || null,
        imagePreview: currentSpeaker.imagePreview
      };
    } else {
      // Add new speaker
      const newSpeaker = {
        id: -1,
        name: currentSpeaker.name,
        organization: currentSpeaker.organization,
        organizationRole: currentSpeaker.organizationRole,
        // Store preview for UI and pass binary in callback payload
        image: currentSpeaker.imagePreview
      };
      
      updatedSpeakers = [...speakers, newSpeaker];
      savedSpeaker = {
        ...newSpeaker,
        // Include the binary file for API submission
        image: currentSpeaker.image || null
      };
    }
    
    setSpeakers(updatedSpeakers);
    
    // Call the onSaveSpeakers callback with only the saved speaker (include binary File when available)
    if (onSaveSpeakers) {
      onSaveSpeakers(savedSpeaker);
    }
    
    closeSpeakerDialog();
  };

  const removeSpeaker = (id) => {
    const updatedSpeakers = speakers.filter(speaker => speaker.id !== id);
    setSpeakers(updatedSpeakers);
    
    // Call the onSaveSpeakers callback with null to indicate removal
    if (onRemoveSpeaker) {
      onRemoveSpeaker(id); // or pass the removed speaker ID if needed
    }
  };

  const addTakeaway = () => {
    if (newTakeaway.trim()) {
      const updatedTakeaways = [...keyTakeaways, {
        id: Date.now(),
        text: newTakeaway.trim()
      }];
      
      setKeyTakeaways(updatedTakeaways);
      setNewTakeaway('');
    }
  };

  const removeTakeaway = (id) => {
    const updatedTakeaways = keyTakeaways.filter(takeaway => takeaway.id !== id);
    setKeyTakeaways(updatedTakeaways);
  };

  const handleTakeawayKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTakeaway();
    }
  };

  const handleSaveKeyTakeaways = () => {
    // Call the onSaveKeyTakeaways callback with current key takeaways
    if (onSaveKeyTakeaways) {
      onSaveKeyTakeaways(keyTakeaways);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="body1" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1 }} /> Additional Event Details
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {/* Event Summary Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1">
                Event Summary & Key Takeaways
              </Typography>
              {keyTakeaways.length > 0 && (
                <Button 
                  variant="contained" 
                  onClick={handleSaveKeyTakeaways}
                  startIcon={<SaveIcon />}
                >
                  Save Takeaways
                </Button>
              )}
            </Box>
            
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add key points that attendees will learn or experience at your event
              </Typography>
              
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Add key takeaway"
                  value={newTakeaway}
                  onChange={(e) => setNewTakeaway(e.target.value)}
                  onKeyPress={handleTakeawayKeyPress}
                  sx={{ mr: 1 }}
                />
                <Button 
                  variant="contained" 
                  onClick={addTakeaway}
                  disabled={!newTakeaway.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              
              {keyTakeaways.length > 0 ? (
                <List dense>
                  {keyTakeaways.map((takeaway) => (
                    <ListItem key={takeaway.id} divider>
                      <ListItemIcon>
                        <HighlightIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={takeaway.text} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => removeTakeaway(takeaway.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <HighlightIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No key takeaways added yet. Add important points that attendees will learn.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
          
          {/* Speakers Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1">
                Event Speakers
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => openSpeakerDialog()}
              >
                Add Speaker
              </Button>
            </Box>

            {speakers.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
                <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary" gutterBottom>
                  No speakers added yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add Speaker" to add speakers to your event.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {speakers.map((speaker) => (
                  <Paper key={speaker.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={speaker.image} 
                        sx={{ width: 60, height: 60, mr: 2 }}
                      >
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">{speaker.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {speaker.organizationRole} {speaker.organization && `at ${speaker.organization}`}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton 
                          size="small" 
                          sx={{ mr: 1 }}
                          onClick={() => openSpeakerDialog(speaker)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => removeSpeaker(speaker.id)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Speaker Dialog */}
      <Dialog open={speakerDialogOpen} onClose={closeSpeakerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentSpeaker.id ? 'Edit Speaker' : 'Add Speaker'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Image Upload */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar 
                src={currentSpeaker.imagePreview} 
                sx={{ width: 100, height: 100, mb: 2 }}
              >
                <PersonIcon />
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="speaker-image-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="speaker-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                >
                  Upload Image
                </Button>
              </label>
            </Box>

            {/* Name */}
            <TextField
              fullWidth
              label="Name *"
              name="name"
              value={currentSpeaker.name}
              onChange={handleSpeakerInputChange}
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Organization */}
            <TextField
              fullWidth
              label="Organization"
              name="organization"
              value={currentSpeaker.organization}
              onChange={handleSpeakerInputChange}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Organization Role */}
            <TextField
              fullWidth
              label="Role"
              name="organizationRole"
              value={currentSpeaker.organizationRole}
              onChange={handleSpeakerInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSpeakerDialog}>Cancel</Button>
          <Button 
            onClick={saveSpeaker} 
            variant="contained" 
            disabled={!currentSpeaker.name.trim()}
          >
            {currentSpeaker.id ? 'Update' : 'Save'} Speaker
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAdditionalDetails;